/**
 * PATH: packages/ui/src/kanban/components/KanbanCardActionZone.tsx
 * FILE_ID: UI-KANBAN-CARD-ACTION-ZONE-001
 * PURPOSE: In-flow quick-action zone that the card GROWS to reveal on hover —
 *   the promoted AI-thread trigger + a "more" affordance — instead of floating
 *   an absolute rail over the corner.
 * WHY:
 *   FEAT-2026-06-20-KANBAN-CARD-HOVER-ZONE — operator (2026-06-20): "you don't
 *   need [to] overlay the animation … nothing stopping you from expanding the
 *   area on which the features are … random empty strip … wasting [space]". So
 *   hover ADDS a real row (grid-rows 0fr→1fr → the card height absorbs it);
 *   nothing floats, nothing fades, there is no corner overlap to dodge. The AI
 *   thread is PROMOTED here as a labelled pill (was a 14px right-edge tab).
 * FLOW:
 *   ┌───────────────┐  hover   ┌────────────────────────┐   ┌─────────────────┐
 *   │ visible=false │ ───────▶ │ grid-rows 0fr→1fr grow  │ → │ [thread] · [⋯]  │
 *   │ (collapsed)   │          │ (in flow, no overlay)   │   │ in-flow buttons │
 *   └───────────────┘          └────────────────────────┘   └─────────────────┘
 *   Phones have no hover AND no room to waste, so instead of the full rail we
 *   render a single labelled "⋯ Actions" pill that opens the same rich context
 *   menu (BUG-2026-06-23-KANBAN-CARD-MOBILE-FAT-ROW — the always-open rail was
 *   the "fat row"; the bare ghost kebab that replaced it was undiscoverable, so
 *   it now carries a label + a ≥44px touch target).
 * DEPENDENCIES:
 *   - lucide-react MoreHorizontal: the "more" glyph.
 *   - ../useIsPhone: swap the hover rail for a compact "⋯" on touch.
 * RELATED:
 *   - ./KanbanCard.tsx: renders this below the footer; passes the thread trigger
 *     (KanbanCardPeekHandle, now in-flow) as `threadSlot`.
 *   - ./KanbanCardPeekHandle.tsx: the promoted thread pill + its slide-in peek.
 */
import { useEffect, useRef, type ReactNode } from 'react'
import { MoreHorizontal } from 'lucide-react'

import { useIsPhone } from '../useIsPhone'
import { CardActionButton } from '../card-surface/controls/CardActionButton'
import { CardActionCluster } from '../card-surface/controls/CardActionCluster'

/** Nudge the lane scroller so an expanded action zone stays in view. */
function ensureActionZoneVisible(zone: HTMLElement): void {
  const scroller = zone.closest('[data-lane-scroll]') as HTMLElement | null
  if (!scroller) return
  const zoneRect = zone.getBoundingClientRect()
  const scrollRect = scroller.getBoundingClientRect()
  const overflowBottom = zoneRect.bottom - scrollRect.bottom
  if (overflowBottom > 0) {
    scroller.scrollTop += overflowBottom + 8
  }
}

export interface KanbanCardActionZoneProps {
  /** Reveal the zone (card hover). Suppressed mid-drag / drop-hover upstream. */
  visible: boolean
  /** Open the card context menu anchored at the button (same as right-click).
   *  Omit to hide the "more" button. */
  onMore?: (anchor: { x: number; y: number }) => void
  /** The promoted AI-thread trigger (KanbanCardPeekHandle, rendered in-flow).
   *  Omit when the card has no linked email / the host wires no thread API. */
  threadSlot?: ReactNode
  /** FEAT-2026-06-20-KANBAN-CARD-ACTION-RAIL — the left-aligned inline
   *  quick-action buttons (priority / due / assign / move / archive,
   *  KanbanCardQuickActions). Omit to keep the zone `…` + Thread only. */
  quickActionsSlot?: ReactNode
  /** FEAT-2026-06-24-KANBAN-CARD-QUICK-CAPTURE — a slim "note / date…" input
   *  (KanbanCardQuickCapture) rendered as its own full-width row below the
   *  button rail. Hover reveals it; the zone's `focus-within` keeps it open
   *  while the operator types. Omit to render the zone exactly as before. */
  quickCaptureSlot?: ReactNode
}

// `scale-110` (not the old `scale-150`) so a hovered glyph stays inside its own
// box and never overlaps the rail beside it (BUG-2026-06-30-KANBAN-CARD-ACTION-
// OVERLAP). Fixed h-7 w-7 so it rides at the same height as the quick-action
// capsule and the Thread pill (FEAT-2026-07-02-KANBAN-CARD-ACTION-CAPSULE).
const MORE_BTN =
  'inline-flex h-7 w-7 items-center justify-center rounded-md text-neutral-500 transition-[transform,background-color,color] duration-150 hover:scale-110 hover:bg-neutral-100 hover:text-neutral-800 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-100'

/** In-flow hover-grow action zone. Pure presentational — all open/close timing
 *  lives in the children (peek handle) / parent (context menu). Returns null
 *  when there is nothing to show so read-only callers render byte-identically. */
export function KanbanCardActionZone({
  visible,
  onMore,
  threadSlot,
  quickActionsSlot,
  quickCaptureSlot,
}: KanbanCardActionZoneProps) {
  const isPhone = useIsPhone()
  const zoneRef = useRef<HTMLDivElement | null>(null)
  const hasContent = !!(onMore || threadSlot || quickActionsSlot || quickCaptureSlot)
  const shown = !isPhone && visible && hasContent

  // Last card in a lane: growing the zone in-flow pushes the rail below the
  // scrollport. Scroll the lane (not the page) so options stay clickable.
  useEffect(() => {
    if (!shown || !zoneRef.current) return
    const id = window.requestAnimationFrame(() => {
      if (zoneRef.current) ensureActionZoneVisible(zoneRef.current)
    })
    return () => window.cancelAnimationFrame(id)
  }, [shown])

  if (!hasContent) return null

  // BUG-2026-06-23-KANBAN-CARD-MOBILE-FAT-ROW — phones have no hover, so the
  // old `shown = visible || isPhone` left the FULL action rail (priority / due /
  // assign / move / archive + the Thread pill) permanently expanded on EVERY
  // card. Each card carried a tall toolbar strip — the "fat row" operators hit
  // on touch — burning the scarce vertical space a phone board most needs.
  // On touch every one of those actions is ALREADY reachable: long-press opens
  // the rich context menu and a tap opens the detail drawer (which carries the
  // email + thread). So on phones we collapse the zone to a single slim "⋯"
  // that opens that SAME context menu — one obvious tap target, no fat rail.
  if (isPhone) {
    if (!onMore) return null
    // A labelled "⋯ Actions" pill — a real ≥44px touch target with a visible
    // label so the rich action menu is DISCOVERABLE on touch (operators were
    // missing the bare ghost kebab → "so many actions missing on mobile"). It
    // opens the same context menu as long-press / right-click.
    return (
      <div
        data-card-action-zone=""
        data-shown="true"
        data-variant="phone"
        className="mt-1.5 flex justify-end"
      >
        <button
          type="button"
          data-card-action="more"
          aria-label="More actions"
          title="More actions"
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            const r = e.currentTarget.getBoundingClientRect()
            // Anchor at the button's RIGHT edge: on phones the menu is a
            // full-width bottom sheet (anchor ignored), but on small tablets
            // that still resolve `useIsPhone` the popover then opens flush to
            // the card's action corner instead of off-screen-left.
            onMore({ x: r.right, y: r.bottom })
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
          className="inline-flex min-h-[36px] items-center gap-1.5 rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-1.5 text-xs font-medium text-neutral-600 transition-transform active:scale-95 active:bg-neutral-100 dark:border-zinc-700 dark:bg-zinc-800/70 dark:text-zinc-300 dark:active:bg-zinc-800"
        >
          <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
          Actions
        </button>
      </div>
    )
  }

  // Desktop: the zone stays collapsed at rest and the card GROWS to reveal it on
  // hover (grid-rows 0fr→1fr) — no overlay, no reserved empty strip.
  // The "more" kebab + the promoted Thread pill, always rendered as ONE
  // right-hugging cluster. FIX-2026-07-02-KANBAN-CARD-ZONE-TWO-ROWS: with a
  // quick-capture row wired this cluster rides THAT row's free space (beside
  // the input) instead of holding a whole line of its own under the rail;
  // operator: "thread there eat[s] up freespace when it could have been in
  // the same line".
  const rightCluster = (
    <CardActionCluster
      moreSlot={
        onMore ? (
          <CardActionButton
            type="button"
            data-card-action="more"
            aria-label="More actions"
            title="More actions"
            // Not a tab stop while collapsed; it isn't visible at rest.
            tabIndex={shown ? 0 : -1}
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
              const r = e.currentTarget.getBoundingClientRect()
              onMore({ x: r.left, y: r.bottom })
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onDoubleClick={(e) => e.stopPropagation()}
            className={MORE_BTN}
          >
            <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
          </CardActionButton>
        ) : undefined
      }
      threadSlot={threadSlot}
    />
  )

  return (
    <div
      ref={zoneRef}
      data-card-action-zone=""
      data-shown={shown ? 'true' : undefined}
      // grid-rows 0fr→1fr is the height-auto animation: the inner cell collapses
      // to 0 (overflow-hidden) at rest and grows to its content height on hover,
      // so the CARD grows in flow — no absolute overlay, no reserved empty strip.
      // `focus-within:*` keeps the zone OPEN while the quick-capture input (or
      // any focusable child) holds focus, so the field doesn't collapse out from
      // under the operator the instant the pointer drifts off the card mid-type.
      // FIX-2026-07-02-KANBAN-CARD-ZONE-SNAPPY: duration-200 + transition-all
      // read as lag ("such a long time before it loads"); a 120ms grow scoped to
      // the animated properties makes the reveal feel immediate.
      className={[
        'grid transition-[grid-template-rows,opacity,margin] duration-[120ms] ease-out',
        'focus-within:mt-1.5 focus-within:grid-rows-[1fr] focus-within:opacity-100',
        shown ? 'mt-1.5 grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
      ].join(' ')}
    >
      <div className="min-h-0 overflow-hidden">
        {/* Row 1: the quick-action capsule rail. The rail scrolls inside its
            own min-width cell so More/Thread remain one fixed cluster rather
            than wrapping into a detached blank action row. */}
        <div className="flex min-w-0 items-center gap-x-2 border-t border-neutral-100 pt-1.5 dark:border-zinc-800">
          <div className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto">
            {quickActionsSlot}
          </div>
          {!quickCaptureSlot && rightCluster}
        </div>
        {/* Row 2: FEAT-2026-06-24-KANBAN-CARD-QUICK-CAPTURE input, sharing its
            line with the more/Thread cluster so the zone is TWO rows, not three
            (the input shrinks first; the cluster never wraps or clips). */}
        {quickCaptureSlot && (
          <div className="mt-1.5 flex items-center gap-1.5">
            <div className="min-w-0 flex-1">{quickCaptureSlot}</div>
            {rightCluster}
          </div>
        )}
      </div>
    </div>
  )
}
